import { memo, useCallback, useMemo } from 'react'
import Form from 'react-bootstrap/Form'
import { PrimaryButton } from '../../components/Button'
import OptionPillGroup from '../../components/OptionsChip/OptionChip'
import { usePostAppointmentContext } from '../../context/PostAppointmentContext/PostAppointmentProvider'
import { NoFollowUpReasonEnum } from './Orders'

export const options = [
  // Options values should be in the format of 'X-weeks' or 'X-months'
  {
    label: '4 weeks',
    value: '4-weeks',
  },
  {
    label: '6 weeks',
    value: '6-weeks',
  },
  {
    label: '8 weeks',
    value: '8-weeks',
  },
  {
    label: '12 weeks',
    value: '12-weeks',
  },
  {
    label: '6 months',
    value: '6-months',
  },
  {
    label: '12 months / PRN',
    value: '12-months',
  },
]

const getTodayIsoDate = () => {
  const today = new Date()
  const yyyy = today.getFullYear()
  const mm = String(today.getMonth() + 1).padStart(2, '0')
  const dd = String(today.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const Timeline: React.FC = () => {
  const {
    nextScreen,
    updateTimeline,
    timeline,
    timelineDate,
    isEditing,
    noFollowUpReason,
    noFollowUpOtherReason,
    updateEditNoFollowup,
    updateNoFollowUpOtherReason,
  } = usePostAppointmentContext()

  const handleNext = useCallback(() => nextScreen(), [])

  // A note signed with an "Other" reason stores the free text, not the literal
  // "Other", so without this the select falls back to its first option and
  // claims the note still has a follow-up.
  const reasonOptions = useMemo(() => {
    const known = Object.values(NoFollowUpReasonEnum) as string[]
    return noFollowUpReason && !known.includes(noFollowUpReason)
      ? [...known, noFollowUpReason]
      : known
  }, [noFollowUpReason])

  const otherReasonMissing =
    noFollowUpReason === NoFollowUpReasonEnum.OTHER &&
    !noFollowUpOtherReason.trim()

  // An edit that changes only the note text must be able to walk past this
  // step, so "nothing chosen" means "leave the follow-up as signed".
  const nextDisabled = isEditing
    ? otherReasonMissing
    : !timeline && !timelineDate

  return (
    <div className="d-flex justify-content-center align-items-center flex-column gap-5 mt-3">
      <div className="timeline d-flex flex-column gap-5">
        <p className="m-0">
          <strong>When would you like to see this patient next?</strong>
        </p>

        <OptionPillGroup
          options={options}
          selected={timeline}
          onChange={(value) => updateTimeline(value, false)}
        />

        <div className="divider-with-text">
          <div className="line" />
          <span className="text">or</span>
          <div className="line" />
        </div>

        <div
          className="date-input-card d-flex flex-column"
          onClick={() => updateTimeline('', false)}
        >
          <strong className="label">Select Date</strong>
          {timeline ? (
            <input
              type="text"
              value=""
              placeholder="mm/dd/yyyy"
              className="custom-date-input"
            />
          ) : (
            <input
              type="date"
              min={getTodayIsoDate()}
              onChange={(e) => {
                if (new Date(e.target.value) > new Date()) {
                  updateTimeline(e.target.value, true)
                } else {
                  updateTimeline('', true)
                }
              }}
              className="custom-date-input"
            />
          )}
        </div>

        {isEditing && (
          <div className="d-flex flex-column gap-2">
            <p className="label mb-0">Or record no follow-up</p>
            <Form.Select
              aria-label="No follow-up reason"
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                updateEditNoFollowup(event.target.value)
              }
              value={noFollowUpReason ?? ''}
            >
              <option value="">Keep the follow-up as signed</option>
              {reasonOptions.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </Form.Select>
            {noFollowUpReason === NoFollowUpReasonEnum.OTHER && (
              <Form.Control
                id="editNoFollowUpOtherReason"
                onChange={updateNoFollowUpOtherReason}
                placeholder="Reason"
                type="text"
                value={noFollowUpOtherReason}
              />
            )}
          </div>
        )}

        <div className="d-flex justify-content-end align-items-center">
          <PrimaryButton
            onClick={handleNext}
            disabled={nextDisabled}
            theme="dark"
          >
            Next
          </PrimaryButton>
        </div>
      </div>
    </div>
  )
}

export default memo(Timeline)
